import { Component, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AuthStore } from '../../core/store/auth.store';
import { PermissionService } from '../../core/services/permission.service';
import { CompanyContextStore } from '../../core/store/company-context.store';
import { TenantService } from '../../core/services/tenant.service';
import { BusinessService } from '../../core/services/business.service';
import { ToastService } from '../../core/services/toast.service';

interface TopbarNotification {
  id: string;
  title: string;
  time: string;
}

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    PopoverModule,
    ButtonModule,
    BadgeModule,
    OverlayBadgeModule,
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  private authStore     = inject(AuthStore);
  private permService   = inject(PermissionService);
  private router        = inject(Router);
  private tenantService   = inject(TenantService);
  private businessService = inject(BusinessService);
  private toastService    = inject(ToastService);
  readonly companyCtx     = inject(CompanyContextStore);

  readonly doQuery    = signal('');
  readonly searchingDo = signal(false);

  constructor() {
    this.hydrateCompanyLogo();
  }

  readonly userName = this.authStore.userDisplayName;
  readonly userRole = computed(() => this.authStore.userRoleNames()[0] ?? 'Sin rol');

  /** Muestra el logo del cliente solo cuando hay un contexto de empresa activo
   *  o cuando el usuario no es superadministrador. */
  readonly showClientLogo = computed(() =>
    !this.permService.isSuperAdmin() || this.companyCtx.isInContext(),
  );

  /** En vista administrador usa el logo del tenant objetivo; si no, el del login. */
  readonly companyLogo = computed(() =>
    this.companyCtx.isInContext()
      ? this.companyCtx.companyLogoUrl()
      : this.authStore.companyLogo(),
  );

  /** La firma de GCS caduca a la hora: se recuerda qué URL falló para reintentar si llega otra. */
  private readonly failedLogoUrl = signal<string | null>(null);
  readonly showCompanyLogoImage = computed(() => {
    const url = this.companyLogo();
    return !!url && url !== this.failedLogoUrl();
  });

  readonly showDoSearch = computed(() => this.permService.isInWorkspaceMode());

  onDoQueryInput(value: string): void {
    this.doQuery.set(value);
  }

  onDoSearch(): void {
    const raw = this.doQuery().trim();
    if (!raw || this.searchingDo()) return;

    if (!/^\d+$/.test(raw)) {
      this.toastService.info('Sin resultados.');
      return;
    }

    this.searchingDo.set(true);
    this.businessService
      .getBusinessList(1, 2, 'Importación', { consecutives: [raw] })
      .subscribe({
        next: (res) => {
          this.searchingDo.set(false);
          if (res.total === 0 || res.items.length === 0) {
            this.toastService.info('Sin resultados.');
            return;
          }
          if (res.total === 1) {
            this.router.navigate(['/app/imports', res.items[0].id]);
            return;
          }
          this.router.navigate(['/app/imports'], { queryParams: { consecutives: raw } });
        },
        error: () => {
          this.searchingDo.set(false);
          this.toastService.info('Sin resultados.');
        },
      });
  }

  onCompanyLogoError(): void {
    this.failedLogoUrl.set(this.companyLogo());
  }

  readonly userAvatarUrl = computed(() => {
    const name = encodeURIComponent(this.authStore.userDisplayName());
    return `https://ui-avatars.com/api/?name=${name}&size=128&background=e0e7ff&color=1e3a8a`;
  });

  readonly notifications: TopbarNotification[] = [
    { id: '1', title: 'Se completó la importación del archivo', time: 'Hace 10 min' },
    { id: '2', title: 'Hay actualizaciones pendientes en tu cuenta', time: 'Hace 1 h' },
  ];

  get notificationCount(): number {
    return this.notifications.length;
  }

  exitCompany(): void {
    const companyId = this.companyCtx.companyId();
    this.companyCtx.exit();
    if (companyId) {
      this.router.navigate(['/app/admin/company', companyId, 'information']);
    } else {
      this.router.navigate(['/app/admin/company/list']);
    }
  }

  /** Sesiones ya abiertas no tenían el logo en el contexto: se pide al detalle del tenant. */
  private hydrateCompanyLogo(): void {
    const companyId = this.companyCtx.companyId();
    if (!companyId || this.companyCtx.companyLogoUrl()) return;
    this.tenantService.getTenantLogoUrl(companyId).subscribe({
      next: (url) => this.companyCtx.setLogoUrl(url),
    });
  }
}
